import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DarkNavSurface, DarkNavPanel } from "@/components/nav/dark-nav-surface";
import { LanguageSwitcher } from "@/components/language-switcher";
import { MobileMarketingHeader } from "./mobile/mobile-marketing-header";
import { cn } from "@/lib/utils";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

type NavLink = { href: string; label: string; internal: boolean };

export function MarketingHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Grouped: Product first, Trust, then Commercial anchors
  const productLinks: NavLink[] = [
    { href: "/features", label: t("nav.features"), internal: true },
    { href: "/moduli", label: t("nav.modules"), internal: true },
    { href: "/demo", label: t("nav.demo"), internal: true },
  ];
  const trustLinks: NavLink[] = [
    { href: "/sicurezza", label: t("nav.security"), internal: true },
  ];
  const commercialLinks: NavLink[] = [
    { href: "/#pricing", label: t("nav.pricing"), internal: false },
    { href: "/#faq", label: t("nav.faq"), internal: false },
  ];

  const allMobileLinks: NavLink[] = [...productLinks, ...trustLinks, ...commercialLinks];

  const isActive = (href: string) => href.startsWith("/#") ? false : pathname === href;

  const renderLink = (l: NavLink) => {
    const active = isActive(l.href);
    const cls = cn(
      "text-[11px] font-medium uppercase tracking-[0.16em] transition-colors",
      active ? "text-teal-400" : "text-white/55 hover:text-teal-400",
    );
    return l.internal ? (
      <Link
        key={l.href}
        to={l.href as any}
        className={cls}
        activeProps={{ className: "text-teal-400" }}
        activeOptions={{ exact: true }}
      >
        {l.label}
      </Link>
    ) : (
      <a key={l.href} href={l.href} className={cls}>
        {l.label}
      </a>
    );
  };

  return (
    <>
      <MobileMarketingHeader />
      <DarkNavSurface className="fixed inset-x-0 top-0 z-50 border-b border-white/10 pt-safe py-3 hidden md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
          {/* Editorial masthead wordmark */}
          <Link to="/" aria-label="lexaipro" className="flex shrink-0 items-baseline gap-2">
            <span className="font-editorial text-2xl font-bold tracking-[-0.01em] text-white">
              Lex<span className="text-teal-400">AI</span><span className="italic font-normal text-white/60">Pro</span>
            </span>
            <span
              suppressHydrationWarning
              className="hidden border border-teal-500/30 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.18em] text-teal-400 lg:inline"
            >
              {t("nav.beta")}
            </span>
          </Link>

          {/* Center nav, grouped with hairline dividers */}
          <nav className="hidden flex-1 items-center justify-center gap-6 md:flex">
            {productLinks.map(renderLink)}
            <span aria-hidden className="h-3 w-px bg-white/15" />
            {trustLinks.map(renderLink)}
            <span aria-hidden className="h-3 w-px bg-white/15" />
            {commercialLinks.map(renderLink)}
          </nav>

          {/* Right cluster */}
          <div className="hidden shrink-0 items-center gap-2 md:flex">
            <LanguageSwitcher variant="dark" />
            <Button variant="on-dark-ghost" size="pill" onClick={() => openWhatsAppContact()}>
              {t("nav.contactSales")}
            </Button>
            <Button asChild variant="on-dark-ghost" size="pill">
              <Link to="/login">{t("nav.login")}</Link>
            </Button>
            <Button asChild variant="on-dark" size="pill">
              <Link to="/signup">{t("nav.signup")}</Link>
            </Button>
          </div>


          {/* Mobile fallback (md hides outer block, but keep symmetry) */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageSwitcher variant="dark" />
            <Button asChild variant="on-dark" size="pill" className="h-8 px-3 text-[12px]">
              <Link to="/signup">{t("nav.signup")}</Link>
            </Button>
            <button
              aria-label={t("nav.menu")}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="ios-tap rounded-full bg-white/10 p-2 text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <>
            <div
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 top-0 z-40 bg-black/60 md:hidden"
            />
            <DarkNavPanel className="fixed inset-x-3 top-[calc(env(safe-area-inset-top)+56px)] z-50 overflow-hidden rounded-3xl p-2 md:hidden">
              <nav className="flex flex-col">
                {allMobileLinks.map((l) =>
                  l.internal ? (
                    <Link
                      key={l.href}
                      to={l.href as any}
                      onClick={() => setMobileOpen(false)}
                      className="ios-tap rounded-2xl px-4 py-3.5 text-[17px] font-medium hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={() => setMobileOpen(false)}
                      className="ios-tap rounded-2xl px-4 py-3.5 text-[17px] font-medium hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      {l.label}
                    </a>
                  ),
                )}
              </nav>
              <div className="mt-2 flex flex-col gap-2 border-t border-white/10 px-2 pt-3 pb-2">
                <Button variant="on-dark-outline" size="pill-lg" onClick={() => { setMobileOpen(false); openWhatsAppContact(); }}>
                  {t("nav.contactSales")}
                </Button>
                <Button asChild variant="on-dark-outline" size="pill-lg">
                  <Link to="/login">{t("nav.login")}</Link>
                </Button>
                <Button asChild variant="on-dark" size="pill-lg">
                  <Link to="/signup">{t("nav.signup")}</Link>
                </Button>
              </div>
            </DarkNavPanel>
          </>
        )}
      </DarkNavSurface>
    </>
  );
}
